import moment from 'moment'
import type { ActivityType, DisplayEntry } from '../../types'
import { DATETIME_FORMAT } from '../../utils/calc'

interface EntryCardProps {
  entry: DisplayEntry
  onEdit: (type: ActivityType, index: number) => void
  onCopy: (type: ActivityType, index: number) => void
  onDelete: (type: ActivityType, index: number) => void
}

const BADGE: Record<ActivityType, { cls: string; letter: string }> = {
  cardio: { cls: 'badge-cardio', letter: 'C' },
  weights: { cls: 'badge-weights', letter: 'W' },
  exercise: { cls: 'badge-exercise', letter: 'E' },
}

const get = (e: DisplayEntry, k: string): string =>
  (e as unknown as Record<string, string>)[k] || ''

function details(entry: DisplayEntry): { label: string; value: string }[] {
  const out: { label: string; value: string }[] = []
  const push = (label: string, value: string) => {
    if (value) out.push({ label, value })
  }
  if (entry.type === 'cardio') {
    push('Level', get(entry, 'level'))
    push('Gradient', get(entry, 'gradient'))
    push('Duration', get(entry, 'duration'))
    push('Calories', get(entry, 'calories'))
    push('Distance', get(entry, 'distance') && `${get(entry, 'distance')} km`)
  } else if (entry.type === 'weights') {
    push('Weight', get(entry, 'kg') && `${get(entry, 'kg')} kg`)
    push('Reps', get(entry, 'repetitions'))
    push('Duration', get(entry, 'duration'))
    push('Calories', get(entry, 'calories'))
  } else {
    push('Intensity', get(entry, 'intensity') && `MET ${get(entry, 'intensity')}`)
    push('Duration', get(entry, 'duration'))
    push('Distance', get(entry, 'distance') && `${get(entry, 'distance')} km`)
    push('Calories', get(entry, 'calories'))
  }
  return out
}

export default function EntryCard({ entry, onEdit, onCopy, onDelete }: EntryCardProps) {
  const badge = BADGE[entry.type]
  const time = moment(entry.datetime, DATETIME_FORMAT).format('h:mm a')
  const notes = get(entry, 'notes')

  return (
    <div className={`card card-${entry.type} relative`}>
      <div className="flex items-start gap-3">
        <span className={`badge ${badge.cls} shrink-0 mt-0.5`}>{badge.letter}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-semibold text-[var(--text-primary)] truncate">
              {entry.displayName || '—'}
            </h3>
            <span className="text-xs text-[var(--text-secondary)] shrink-0">{time}</span>
          </div>

          {details(entry).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--text-secondary)]">
              {details(entry).map((d) => (
                <span key={d.label}>
                  <span className="opacity-70">{d.label}:</span>{' '}
                  <span className="text-[var(--text-primary)]">{d.value}</span>
                </span>
              ))}
            </div>
          )}

          {notes && <p className="mt-2 text-sm text-[var(--text-secondary)] italic">{notes}</p>}

          <div className="mt-3 flex gap-3 text-xs">
            <button className="text-[var(--accent-teal)] hover:underline" onClick={() => onEdit(entry.type, entry.id)}>
              Edit
            </button>
            <button className="text-[var(--text-secondary)] hover:underline" onClick={() => onCopy(entry.type, entry.id)}>
              Copy
            </button>
            <button className="text-[var(--bac-danger)] hover:underline" onClick={() => onDelete(entry.type, entry.id)}>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
